import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="tooltip"
//
// Replaces qtip2 with Tippy.js for tooltips
// Note: Tippy.js loaded via CDN (global window.tippy variable)
//
// Usage:
//   span.has-tooltip data-controller="tooltip" data-tooltip-content-value="Help text" Help
//
// Or with positioning:
//   span.has-tooltip(
//     data-controller="tooltip"
//     data-tooltip-content-value="Help text"
//     data-tooltip-placement-value="top"
//   ) Help
//
// Values:
//   - content: Tooltip text content (required)
//   - placement: Position ('top', 'bottom', 'left', 'right', 'auto') - default: 'top'
//   - theme: Tippy theme ('light', 'light-border', 'material', 'translucent') - default: 'light-border'
//
export default class extends Controller {
  static values = {
    content: String,           // Tooltip content text
    placement: { type: String, default: "top" },  // Tooltip position
    theme: { type: String, default: "light-border" },  // Tippy theme
    onlyWhenCollapsed: { type: Boolean, default: false },  // Only show when sidebar is collapsed
    onlyWhenTruncated: { type: Boolean, default: false },  // Only show when text is truncated (ellipsis)
    // touch: enable on touch devices. `true` (default) matches Tippy's
    // built-in behaviour (tap shows tooltip, click still fires). Set
    // to `false` on action buttons that need a single tap to activate
    // without the tooltip getting in the way (the tooltip stays
    // hover-only on desktop). Mirrors Tippy's `touch` option.
    touch: { type: Boolean, default: true }
  }

  connect() {
    // If tooltip should only show when collapsed, conditionally create it
    if (this.onlyWhenCollapsedValue) {
      this.createConditionalTooltip()
    } else if (this.onlyWhenTruncatedValue) {
      this.createTruncatedTooltip()
    } else {
      // Always create tooltip
      this.createTooltip()
    }
  }

  createTooltip() {
    // Create Tippy instance
    this.tippy = tippy(this.element, {
      content: this.contentValue,
      placement: this.placementValue,
      theme: this.themeValue,
      arrow: true,
      animation: 'fade',
      duration: [200, 150],  // [show, hide] in ms
      interactive: false,    // Tooltip disappears when mouse leaves trigger
      allowHTML: true,       // Allow HTML in tooltip content (for <hr> separators, etc.)
      touch: this.touchValue
    })
  }

  createTruncatedTooltip() {
    // Create tooltip that only shows when text is truncated (has ellipsis)
    this.tippy = tippy(this.element, {
      content: this.contentValue,
      placement: this.placementValue,
      theme: this.themeValue,
      arrow: true,
      animation: 'fade',
      duration: [200, 150],
      interactive: false,
      allowHTML: true,
      touch: this.touchValue,
      onShow: (instance) => {
        // Check if text is actually truncated (scrollWidth > clientWidth)
        const el = this.element
        if (el.scrollWidth <= el.clientWidth) {
          return false // Prevent tooltip from showing - text is not truncated
        }
      }
    })
  }

  createConditionalTooltip() {
    // Create tooltip that only shows when sidebar is collapsed
    this.tippy = tippy(this.element, {
      content: this.contentValue,
      placement: this.placementValue,
      theme: this.themeValue,
      arrow: true,
      animation: 'fade',
      duration: [200, 150],
      interactive: false,
      allowHTML: true,
      touch: this.touchValue,
      onShow: (instance) => {
        // Check if sidebar is collapsed
        const sidebar = document.querySelector('.app-sidebar')
        if (!sidebar || !sidebar.classList.contains('collapsed')) {
          return false // Prevent tooltip from showing
        }
      }
    })
  }

  disconnect() {
    // Clean up Tippy instance when controller disconnects
    if (this.tippy) {
      this.tippy.destroy()
      this.tippy = null
    }
  }

  // Update tooltip content if value changes
  contentValueChanged() {
    if (this.tippy) {
      this.tippy.setContent(this.contentValue)
    }
  }

  // Update placement if value changes
  placementValueChanged() {
    if (this.tippy) {
      this.tippy.setProps({ placement: this.placementValue })
    }
  }
}
